import type {
  DocumentViewResponse,
  TemplateSection,
} from "../types/reportTemplate";
import type { ReportFileAttachment } from "../components/workflows/ReportFilePage";

export interface PositionedReportFile extends ReportFileAttachment {
  storageKey?: string;
  afterReportVersion: number;
  kind: "case_file" | "external_baseline";
}

export interface ReportComposition {
  schemaVersion: "report-composition-v1";
  files: PositionedReportFile[];
  /** Pure external-baseline cases render their report supplements after Castle. */
  includeMainReport?: boolean;
}

export type ReportCompositionPart =
  | {
      id: string;
      kind: "report";
      reportData: DocumentViewResponse;
      main: boolean;
    }
  | { id: string; kind: "file"; file: PositionedReportFile };

type AttachedDocument = DocumentViewResponse & {
  attachedReport?: { reportData: DocumentViewResponse } | null;
};

export function compositionReportData(
  data: AttachedDocument
): DocumentViewResponse {
  if (!data.attachedReport) return data;
  const base = data.attachedReport.reportData;
  const entries = new Map(base.addendums.map((entry) => [entry.id, entry]));
  data.addendums.forEach((entry) => entries.set(entry.id, entry));
  const footer = data.template.templateContent.sections.find(
    (section) => section.type === "disclaimers"
  );
  const sections = base.template.templateContent.sections;
  return {
    ...base,
    addendums: Array.from(entries.values()),
    cptCodes: [...base.cptCodes, ...data.cptCodes],
    icdCodes: [...base.icdCodes, ...data.icdCodes],
    template: {
      ...base.template,
      templateContent: {
        ...base.template.templateContent,
        sections:
          footer && !sections.some((section) => section.type === "disclaimers")
            ? [...sections, footer]
            : sections,
      },
    },
  };
}

/** Both review and capture consume this exact ordered sequence. */
export function buildReportCompositionParts(
  data: DocumentViewResponse,
  composition: ReportComposition
): ReportCompositionPart[] {
  let files = [...composition.files].sort(
    (a, b) => a.afterReportVersion - b.afterReportVersion
  );
  const parts: ReportCompositionPart[] = [];
  const includeMainReport = composition.includeMainReport !== false;
  let after = 0;
  if (!includeMainReport) {
    // Castle is first, but its normalized boundary can equal the first pure v1 addendum.
    const externalBaseline = files.find(
      (file) => file.kind === "external_baseline"
    );
    if (externalBaseline) {
      parts.push({
        id: `file:${externalBaseline.id}`,
        kind: "file",
        file: externalBaseline,
      });
      files = files.filter((file) => file.id !== externalBaseline.id);
    }
  }
  files.forEach((file) => {
    appendReportPart(parts, data, {
      after,
      through: file.afterReportVersion,
      includeMainReport,
    });
    parts.push({ id: `file:${file.id}`, kind: "file", file });
    after = file.afterReportVersion;
  });
  appendReportPart(parts, data, {
    after,
    through: Infinity,
    includeMainReport,
  });
  return parts;
}

function appendReportPart(
  parts: ReportCompositionPart[],
  data: DocumentViewResponse,
  range: {
    after: number;
    through: number;
    includeMainReport: boolean;
  }
): void {
  const main =
    range.includeMainReport && !parts.some((part) => part.kind === "report");
  const addendums = data.addendums.filter((entry) =>
    range.includeMainReport
      ? entry.signOffType === "addendum"
        ? entry.reportVersion > range.after &&
          entry.reportVersion <= range.through
        : main
      : entry.reportVersion > range.after &&
        entry.reportVersion <= range.through
  );
  if (!main && !addendums.length) return;
  const hasLaterAddendums = data.addendums.some(
    (entry) =>
      entry.reportVersion > range.through &&
      (range.includeMainReport ? entry.signOffType === "addendum" : true)
  );
  parts.push({
    id: main ? "report:main" : `report:${range.after}`,
    kind: "report",
    main,
    reportData: reportPartData(data, {
      addendums,
      main,
      hasLaterAddendums,
      hasNonAddendumSupplement: addendums.some(
        (entry) => entry.signOffType !== "addendum"
      ),
    }),
  });
}

function reportPartData(
  data: DocumentViewResponse,
  part: {
    addendums: DocumentViewResponse["addendums"];
    main: boolean;
    hasLaterAddendums: boolean;
    hasNonAddendumSupplement: boolean;
  }
): DocumentViewResponse {
  const content = data.template.templateContent;
  const addendumSection =
    content.sections.find((section) => section.type === "addendum") ||
    ({
      type: "addendum",
      label: "Addendum",
      config: { showCodes: true },
    } as TemplateSection);
  const sections =
    part.main || part.hasNonAddendumSupplement
      ? part.main
        ? mainReportSections(data)
        : content.sections
      : [
          addendumSection,
          ...content.sections.filter((s) => s.type === "disclaimers"),
        ];
  return {
    ...data,
    addendums: part.addendums,
    template: {
      ...data.template,
      templateContent: {
        ...content,
        metadata: {
          ...content.metadata,
          ...(!part.main && !part.hasNonAddendumSupplement
            ? { suppressSupplementNotices: true }
            : {}),
          ...(!part.main && part.hasNonAddendumSupplement
            ? { suppressSupplementNotices: false }
            : {}),
        },
        sections: part.hasLaterAddendums
          ? sections.filter((s) => s.type !== "disclaimers")
          : sections,
      },
    },
  };
}

function mainReportSections(data: DocumentViewResponse): TemplateSection[] {
  const sections = data.template.templateContent.sections;
  if (!data.addendums.some((entry) => entry.signOffType === "addendum"))
    return sections;
  const withoutNotice = sections.filter(
    (section) => section.type !== "report_supplement_notices"
  );
  const index = Math.max(
    0,
    withoutNotice.findIndex((section) => section.type === "header") + 1
  );
  const notice: TemplateSection = {
    type: "report_supplement_notices",
    label: "",
    config: {
      reportSupplementFragment: {
        groupType: "addendum",
        showBanner: true,
        showDivider: false,
        showAttribution: false,
      },
    },
  };
  return [
    ...withoutNotice.slice(0, index),
    notice,
    ...withoutNotice.slice(index),
  ];
}
